import React, { Fragment, useState, useEffect } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { FormattedMessageWrappedInSpan } from '../../misc';
import cloneDeep from 'lodash.clonedeep';
import classNames from 'classnames';

import ItemAddedDialog from '../dialogs/ItemAddedDialog';

import { FOTO_FLAT, OTHER_PRODUCTS, GIFTCARD, PRICELIST, PENDING, PENDING_EVENT_GIFTCARD } from '../../../utils/variables';
import {
  includesVideo,
  includesSinglePhoto,
  includesPhotoDvd,
  includesCertificate,
  includesGiftcard,
  getVideo,
  getCertificate,
  getFotoFlat,
  getGiftcard,
} from '../../../utils/common';
import AddGiftcardToCartMutation from '../../../mutations/AddGiftcardToCartMutation';
import withRequestHandler from '../../hoc/withRequestHandler';
import { trackEvent } from '../../../utils/ga-tracking';

import OfferTile from './OfferTile';
import VideoOfferPreviewDialog from '../dialogs/VideoOfferPreviewDialog';
import CertificateOfferPreviewDialog from '../dialogs/CertificateOfferPreviewDialog';
import VoucherOfferPreviewDialog from '../dialogs/VoucherOfferPreviewDialog';
import SectionHeader from '../../misc/SectionHeader';
import HowToBuyOfferHintDialog from '../dialogs/HowToBuyOfferHintDialog';

////////////////////////////////////////////
// TODO missing tests cases:
// resolve mock relay operation with updater, vouchers dialogs
////////////////////////////////////////////

const Pricelist = (props) => {
  const { offer, onScrollToSearchForm } = props;
  const [showVoucherDialog, setShowVoucherDialog] = useState(false);
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [showCertificateDialog, setShowCertificateDialog] = useState(false);
  const [showHowToBuyHintDialog, setShowHowToBuyHintDialog] = useState(false);
  const [videoUrl, setVideoUrl] = useState();
  const [certificatePreviewUrl, setCertificatePreviewUrl] = useState();
  const [updatedCart, setUpdatedCart] = useState(null);
  const [showItemAddedDialog, setShowItemAddedDialog] = useState(false);

  const productsClone = cloneDeep(offer.products);
  const fotoFlat = getFotoFlat(productsClone);
  // After the FotoFlat has been removed from the list of products, the productsClone
  // list contains all further products (in case only the FotoFlat is offered, the list
  // will be empty).
  const showOtherProducts =
    includesSinglePhoto(productsClone) || includesPhotoDvd(productsClone);
  const hasVideo = includesVideo(offer.products);
  const hasCertificates = includesCertificate(offer.products);
  const hasGiftcard = includesGiftcard(productsClone);
  const giftcard = getGiftcard(offer.products);
  const isEventOnline = offer.state !== PENDING;
  const shouldShowOffer = isEventOnline || (!isEventOnline && hasGiftcard);
  
  const getPreviewPhotoForCertificate = (products) => {
    return getCertificate(products).configuration.previewImageUrl;
  };

  const handleCloseVoucherDialog = () => {
    setShowVoucherDialog(false);
  };

  const handleCloseVideoDialog = () => {
    setShowVideoDialog(false);
  };

  const handleCloseCertificateDialog = () => {
    setShowCertificateDialog(false);
  };

  const shouldCenterOfffer = (fotoFlat, allProducts) => {
    if (fotoFlat && allProducts.length === 1) {
      return true;
    }
    if (!fotoFlat && allProducts.length > 0) {
      return true;
    }
    return false;
  };

  const getVideoUrl = (offer) => {
    const videoUrl = getVideo(offer).configuration?.previewVideoUrl;
    return videoUrl;
  };

  const centerOffer = shouldCenterOfffer(fotoFlat, productsClone) || !isEventOnline;

  useEffect(() => {
    if (hasVideo) {
      setVideoUrl(getVideoUrl(offer.products));
    }
    if (hasCertificates) {
      setCertificatePreviewUrl(getPreviewPhotoForCertificate(offer.products));
    }
  }, [hasCertificates, hasVideo, offer.products]);

  const onBuyGiftcard = () => {
    props.onRequestStart();
    AddGiftcardToCartMutation.commit(
      props.relay.environment,
      giftcard.id, // product id
      (error, cart) => {
        if (error) {
          props.onRequestError(error);
        } else {
          setUpdatedCart(cart);
          setShowItemAddedDialog(true);
        }
        props.onRequestStop();
      }
    );
  };

  if (!shouldShowOffer) {
    return null;
  }

 const renderTitle = () => {
   if (isEventOnline) {
     return (
       <FormattedMessageWrappedInSpan
         id="pricelist.title"
         defaultMessage="Offer"
         className="typography bold-text uppercase typography-title-medium"
       />
     );
   } else
     return (
       <FormattedMessageWrappedInSpan
         id="pricelist.giftcardTitle"
         defaultMessage="Buy now as a gift"
         className="typography bold-text uppercase typography-title-medium"
       />
     );
 };

  return (
    <Fragment>
      <div className="offerTile-container">
        <div className="row">
          <div className="col-xs-16 col-sm-12 col-sm-offset-2 subsection-last-in-row">
            <SectionHeader className="mt-20 mb-20">{renderTitle()}</SectionHeader>
          </div>
        </div>

        <div
          className={classNames('row', {
            'center-sm': centerOffer,
          })}
        >
          {!isEventOnline && (
            <>
              <OfferTile
                product={PENDING_EVENT_GIFTCARD}
                componentVersion={PRICELIST}
                fotoFlatPrice={fotoFlat.price}
                hasVideo={hasVideo}
                hasVideoUrl={Boolean(videoUrl)}
                onVideoClick={() => setShowVideoDialog(true)}
                hasCertificates={hasCertificates}
                onCertificateClick={() => setShowCertificateDialog(true)}
                voucherCampaigns={offer.voucherCampaigns}
                onVoucherClick={(voucherCampaign) => {
                  setShowVoucherDialog(voucherCampaign);
                  trackEvent('Event', 'Voucher', 'Offer pricelist');
                }}
                onBuy={() => {
                  onBuyGiftcard();
                }}
              />
              <div className="center-xs col-xs-16 col-sm-12">
                <FormattedMessageWrappedInSpan
                  id="pricelist.descriptionForPendingGiftcardOffer"
                  defaultMessage="The full offer will be available when the event is online."
                  className="small-hint-under-frame"
                />
              </div>
            </>
          )}
          {isEventOnline && (
            <>
              {fotoFlat && (
                <div
                  className={classNames('col-xs-16', 'col-sm-6', {
                    'col-sm-offset-2': !centerOffer,
                  })}
                >
                  <OfferTile
                    product={FOTO_FLAT}
                    componentVersion={PRICELIST}
                    fotoFlatPrice={fotoFlat.price}
                    hasVideo={hasVideo}
                    hasVideoUrl={Boolean(videoUrl)}
                    onVideoClick={() => setShowVideoDialog(true)}
                    hasCertificates={hasCertificates}
                    onCertificateClick={() => setShowCertificateDialog(true)}
                    voucherCampaigns={offer.voucherCampaigns}
                    onVoucherClick={(voucherCampaign) => {
                      setShowVoucherDialog(voucherCampaign);
                      trackEvent('Event', 'Voucher', 'Offer pricelist');
                    }}
                    onPriceClick={() => setShowHowToBuyHintDialog(true)}
                  />
                </div>
              )}
              {(hasGiftcard || showOtherProducts) && (
                <div className="col-sm-6 col-xs-16">
                  {hasGiftcard && (
                    <OfferTile
                      product={GIFTCARD}
                      componentVersion={PRICELIST}
                      fotoFlatPrice={fotoFlat.price}
                      onBuy={() => {
                        onBuyGiftcard();
                      }}
                    />
                  )}
                  {showOtherProducts && (
                    <OfferTile
                      product={OTHER_PRODUCTS}
                      componentVersion={PRICELIST}
                      products={productsClone}
                      onPriceClick={() => setShowHowToBuyHintDialog(true)}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* dialogs */}
      {offer.voucherCampaigns.map((voucherCampaign) => {
        return (
          <VoucherOfferPreviewDialog
            open={
              showVoucherDialog &&
              showVoucherDialog.voucherCampaign &&
              showVoucherDialog.voucherCampaign.id == voucherCampaign.id
            }
            onRequestClose={handleCloseVoucherDialog}
            voucherCampaign={voucherCampaign}
            key={voucherCampaign.id}
          />
        );
      })}
      {hasVideo && videoUrl && (
        <VideoOfferPreviewDialog
          open={showVideoDialog}
          onRequestClose={handleCloseVideoDialog}
          videoUrl={videoUrl}
        />
      )}
      {hasCertificates && (
        <CertificateOfferPreviewDialog
          open={showCertificateDialog}
          onRequestClose={handleCloseCertificateDialog}
          certificatePreviewUrl={certificatePreviewUrl}
        />
      )}
      {updatedCart && (
        <ItemAddedDialog
          open={showItemAddedDialog}
          lineItems={updatedCart.lineItems}
          totalPrice={updatedCart.totalPrice}
          onRequestClose={() => setShowItemAddedDialog(false)}
        />
      )}
      <HowToBuyOfferHintDialog
        open={showHowToBuyHintDialog}
        onRequestClose={() => {
          setShowHowToBuyHintDialog(false);
          onScrollToSearchForm && onScrollToSearchForm();
        }}
      />
    </Fragment>
  );
};

Pricelist.propTypes = {
  offer: PropTypes.object,
  onScrollToSearchForm: PropTypes.func,
  relay: PropTypes.object,
  onRequestStart: PropTypes.func,
  onRequestError: PropTypes.func,
  onRequestStop: PropTypes.func
};

export { Pricelist };

const WrappedComponent = withRequestHandler(Pricelist);

export default createFragmentContainer(WrappedComponent, {
  offer: graphql`
    fragment Pricelist_offer on Event {
      products {
        id
        mediaCollection {
          name
        }
        price
        type
        configuration {
          ... on SingleVideoConfiguration {
            previewVideoUrl
          }
          ... on EventCertificateConfiguration {
            previewImageUrl
          }
        }
      }
      voucherCampaigns {
        id
        banner
        longText
        name
        shortText
        url
      }
      state
    }
  `,
});
